import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import createHttpClient from '../../../services/HttpClient'
import {
  getEquipmentSlotValue,
  setEquipmentSlotValue,
  getEquippedItems,
  isConsumableSlot,
} from './utils/slotUtils'

const httpClient = createHttpClient()

/** Ensure sheet meta exposes both id and characterId (Context uses characterId). */
function normalizeSheetCharacter(character) {
  if (!character) return character
  const id = character.characterId ?? character.id ?? null
  return {
    ...character,
    id: character.id ?? id,
    characterId: character.characterId ?? id,
  }
}

// Async thunks para API calls
export const fetchCharacterInventory = createAsyncThunk('inventory/fetchCharacterInventory', async (characterName, { rejectWithValue }) => {
  try {
    const response = await httpClient.get(`/api/v1/characters/${characterName}/inventory`)
    return response.data
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const equipItem = createAsyncThunk('inventory/equipItem', async ({ characterName, itemId, slotType }, { rejectWithValue }) => {
  try {
    const response = await httpClient.post(`/api/v1/characters/${characterName}/equip`, {
      itemId,
      slotType,
    })
    return response.data
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const unequipItem = createAsyncThunk('inventory/unequipItem', async ({ characterName, slotType }, { rejectWithValue }) => {
  try {
    const response = await httpClient.post(`/api/v1/characters/${characterName}/unequip`, {
      slotType,
    })
    return response.data
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const swapEquipment = createAsyncThunk('inventory/swapEquipment', async ({ characterName, fromSlot, toSlot }, { rejectWithValue }) => {
  try {
    const response = await httpClient.post(`/api/v1/characters/${characterName}/swap-equipment`, {
      fromSlot,
      toSlot,
    })
    return response.data
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const moveItem = createAsyncThunk('inventory/moveItem', async ({ characterName, fromSlot, toSlot }, { rejectWithValue }) => {
  try {
    const response = await httpClient.post(`/api/v1/characters/${characterName}/move-item`, {
      fromSlot,
      toSlot,
    })
    return response.data
  } catch (error) {
    return rejectWithValue(error.message)
  }
})

export const updateCharacterStory = createAsyncThunk(
  'inventory/updateCharacterStory',
  async ({ characterName, descPhysical, descPsychological, history }, { rejectWithValue }) => {
    try {
      const response = await httpClient.put(`/api/v1/characters/${characterName}/story`, {
        desc_physical: descPhysical,
        desc_psychological: descPsychological,
        history,
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error?.message || error.response?.data?.message || 'Error actualizando historia')
    }
  }
)

// Estado inicial — inventory.character = viewed sheet (any slug), not AppContext actor
const initialState = {
  character: {
    id: null,
    characterId: null,
    name: '',
    level: 1,
    experience: { current: 0, max: 1000 },
    stats: {
      strength: { base: 10, bonus: 0 },
      dexterity: { base: 10, bonus: 0 },
      agility: { base: 10, bonus: 0 },
      resistance: { base: 10, bonus: 0 },
      willpower: { base: 10, bonus: 0 },
      chakraControl: { base: 10, bonus: 0 },
      concentration: { base: 10, bonus: 0 },
      intelligence: { base: 10, bonus: 0 },
    },
    alignment: '',
    portrait: '',
    clan: '',
    gender: '',
    province: '',
    shogunato: '',
    element: '',
    dominantHand: '',
    bloodType: '',
    descPhysical: '',
    descPsychological: '',
    history: '',
    bio: '',
    userId: null,
  },
  equipment: {
    head: null,
    chest: null,
    hands: null,
    legs: null,
    feet: null,
    mainHand: null,
    offHand: null,
    ring1: null,
    ring2: null,
    necklace: null,
    earring1: null,
    earring2: null,
    consumables: [null, null, null, null, null],
  },
  inventory: {
    items: [],
    maxSlots: 30,
  },
  combat: {
    damage: { min: 1, max: 8 },
    critical: 2,
    weaponBonus: 0,
    range: 50,
  },
  loading: false,
  error: null,
  draggedItem: null,
  hoveredSlot: null,
  imageCache: {},
}

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    setDraggedItem: (state, action) => {
      state.draggedItem = action.payload
    },
    setHoveredSlot: (state, action) => {
      state.hoveredSlot = action.payload
    },
    clearDragState: (state) => {
      state.draggedItem = null
      state.hoveredSlot = null
    },
    updateImageCache: (state, action) => {
      const { itemId, imageUrl } = action.payload
      state.imageCache[itemId] = imageUrl
    },
    // Acción local para mover items en el inventario (optimistic update)
    moveItemLocal: (state, action) => {
      const { fromIndex, toIndex } = action.payload
      const maxSlots = state.inventory.maxSlots || 30
      const items = Array(maxSlots).fill(null)
      state.inventory.items.forEach((item, index) => {
        if (index < maxSlots) items[index] = item
      })

      if (!items[fromIndex]) return

      const [movedItem] = items.splice(fromIndex, 1)
      items.splice(toIndex, 0, movedItem)
      state.inventory.items = items.slice(0, maxSlots)
    },
    syncInventoryState: (state, action) => {
      const data = action.payload || {}
      if (data.character) state.character = normalizeSheetCharacter(data.character)
      if (data.equipment) state.equipment = data.equipment
      if (data.inventory) state.inventory = data.inventory
      if (data.combat) state.combat = data.combat
    },
    // Acción local para equipar item (optimistic update)
    equipItemLocal: (state, action) => {
      const { item, slotType, inventoryIndex } = action.payload
      const currentlyEquipped = getEquipmentSlotValue(state.equipment, slotType)
      const maxSlots = state.inventory.maxSlots || 30
      const items = normalizeInventoryItems(state.inventory.items, maxSlots)

      if (currentlyEquipped) {
        const freeIndex = items.findIndex((slotItem) => slotItem === null)
        if (freeIndex >= 0) items[freeIndex] = currentlyEquipped
      }

      if (isConsumableSlot(slotType)) {
        setEquipmentSlotValue(state.equipment, slotType, item)
      } else {
        state.equipment[slotType] = item
      }

      if (inventoryIndex !== undefined && inventoryIndex >= 0) {
        items[inventoryIndex] = null
      } else {
        const itemIndex = items.findIndex((slotItem) => slotItem && Number(slotItem.id) === Number(item.id))
        if (itemIndex >= 0) items[itemIndex] = null
      }

      state.inventory.items = items
      state.character.stats = calculateStatsWithEquipment(state.character.stats, state.equipment)
      state.combat = calculateCombatFromEquipment(state.equipment, state.combat)
    },
    // Acción local para desequipar item
    unequipItemLocal: (state, action) => {
      const { slotType } = action.payload
      const equippedItem = getEquipmentSlotValue(state.equipment, slotType)

      if (equippedItem) {
        addItemToInventoryLocal(state.inventory, equippedItem)
        if (isConsumableSlot(slotType)) {
          setEquipmentSlotValue(state.equipment, slotType, null)
        } else {
          state.equipment[slotType] = null
        }
        state.character.stats = calculateStatsWithEquipment(state.character.stats, state.equipment)
        state.combat = calculateCombatFromEquipment(state.equipment, state.combat)
      }
    },
    swapEquipmentLocal: (state, action) => {
      const { fromSlot, toSlot } = action.payload
      const fromItem = getEquipmentSlotValue(state.equipment, fromSlot)
      const toItem = getEquipmentSlotValue(state.equipment, toSlot)
      setEquipmentSlotValue(state.equipment, fromSlot, toItem || null)
      setEquipmentSlotValue(state.equipment, toSlot, fromItem)
      state.character.stats = calculateStatsWithEquipment(state.character.stats, state.equipment)
      state.combat = calculateCombatFromEquipment(state.equipment, state.combat)
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch character inventory
      .addCase(fetchCharacterInventory.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCharacterInventory.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload
        if (data.character) {
          state.character = normalizeSheetCharacter(data.character)
        }
        if (data.equipment) state.equipment = data.equipment
        if (data.inventory) {
          state.inventory = {
            ...data.inventory,
            items: normalizeInventoryItems(data.inventory.items, data.inventory.maxSlots || 30),
          }
        }
        if (data.combat) state.combat = data.combat
      })
      .addCase(fetchCharacterInventory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Equip item
      .addCase(equipItem.pending, (state) => {
        state.loading = true
      })
      .addCase(equipItem.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload
        if (data?.character) state.character = normalizeSheetCharacter(data.character)
        if (data?.equipment) {
          state.equipment = { ...state.equipment, ...data.equipment }
        }
        if (data?.inventory) {
          state.inventory = {
            ...data.inventory,
            items: normalizeInventoryItems(data.inventory.items, data.inventory.maxSlots || 30),
          }
        }
        if (data?.combat) state.combat = data.combat
      })
      .addCase(equipItem.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        // Aquí podríamos revertir el cambio optimista si es necesario
      })

      // Unequip item
      .addCase(unequipItem.pending, (state) => {
        state.loading = true
      })
      .addCase(unequipItem.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload
        if (data?.character) state.character = normalizeSheetCharacter(data.character)
        if (data?.equipment) state.equipment = data.equipment
        if (data?.inventory) state.inventory = data.inventory
        if (data?.combat) state.combat = data.combat
      })
      .addCase(unequipItem.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Swap equipment
      .addCase(swapEquipment.pending, (state) => {
        state.loading = true
      })
      .addCase(swapEquipment.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload
        if (data?.character) state.character = normalizeSheetCharacter(data.character)
        if (data?.equipment) state.equipment = data.equipment
        if (data?.inventory) state.inventory = data.inventory
        if (data?.combat) state.combat = data.combat
      })
      .addCase(swapEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Move item
      .addCase(moveItem.pending, (state) => {
        state.loading = true
      })
      .addCase(moveItem.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload
        if (data?.equipment) state.equipment = data.equipment
        if (data?.inventory) state.inventory = data.inventory
        if (data?.combat) state.combat = data.combat
      })
      .addCase(moveItem.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Update character story
      .addCase(updateCharacterStory.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCharacterStory.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload
        if (data?.character) state.character = normalizeSheetCharacter(data.character)
      })
      .addCase(updateCharacterStory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

const calculateStatsWithEquipment = (baseStats, equipment) => {
  const newStats = { ...baseStats }

  Object.keys(newStats).forEach((stat) => {
    newStats[stat] = { ...newStats[stat], bonus: 0 }
  })

  getEquippedItems(equipment).forEach((item) => {
    if (!item?.stats) return
    Object.keys(item.stats).forEach((stat) => {
      if (newStats[stat]) {
        newStats[stat].bonus += Number(item.stats[stat]) || 0
      }
    })
  })

  return newStats
}

const normalizeInventoryItems = (items = [], maxSlots = 30) => {
  const normalized = Array(maxSlots).fill(null)
  items.forEach((item, index) => {
    if (index < maxSlots) normalized[index] = item
  })
  return normalized
}

const addItemToInventoryLocal = (inventory, item) => {
  const maxSlots = inventory.maxSlots || 30
  const items = normalizeInventoryItems(inventory.items, maxSlots)
  const freeIndex = items.findIndex((slotItem) => slotItem === null)
  if (freeIndex === -1) return false
  items[freeIndex] = item
  inventory.items = items
  return true
}

const calculateCombatFromEquipment = (equipment, currentCombat) => {
  let weaponBonus = 0
  let minDamage = 1
  let maxDamage = 8

  const mainHand = equipment?.mainHand
  if (mainHand?.stats) {
    weaponBonus += Number(mainHand.stats.strength || 0) + Number(mainHand.stats.dexterity || 0)
  }
  if (mainHand?.damage) {
    const parts = String(mainHand.damage).split('-').map((n) => Number(n) || 0)
    minDamage += parts[0] || 0
    maxDamage += parts[1] || 0
  }

  return {
    ...currentCombat,
    damage: { min: minDamage, max: maxDamage },
    weaponBonus,
  }
}

export const {
  setDraggedItem,
  setHoveredSlot,
  clearDragState,
  updateImageCache,
  moveItemLocal,
  equipItemLocal,
  unequipItemLocal,
  swapEquipmentLocal,
  syncInventoryState,
  clearError,
} = inventorySlice.actions

export default inventorySlice.reducer

// Selectores
export const selectCharacter = (state) => state.inventory.character
export const selectEquipment = (state) => state.inventory.equipment
export const selectInventory = (state) => state.inventory.inventory
export const selectCombat = (state) => state.inventory.combat
export const selectLoading = (state) => state.inventory.loading
export const selectError = (state) => state.inventory.error
export const selectDraggedItem = (state) => state.inventory.draggedItem
export const selectHoveredSlot = (state) => state.inventory.hoveredSlot
export const selectImageCache = (state) => state.inventory.imageCache
